import { ButtonContent } from '../ui/ButtonContent';
/**
 * Sidebar trial banner.
 *
 * Rendered in the sidebar between the Recent Interviews list and the org
 * profile section when the active org is on an active Pro trial. Shows the
 * countdown + a primary CTA to add a payment method (keeps Pro after trial
 * end) and, in the final stretch of the trial, an outline Extend button.
 *
 * Gating:
 * - Only shown when `isTrialing` AND the caller has `organization:view_billing`.
 *   The caller (`CollapsibleSidebar`) applies the permission gate so the whole
 *   section is absent for viewer/editor roles.
 * - Extend by 7 days is rendered only when `shouldShowExtendButton` (<= 3
 *   days remaining and the trial is still eligible for the one-time extension)
 *   AND the caller has `organization:manage_billing`.
 *
 * @module components/admin/TrialSidebarBanner
 */
import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import {
  useOrg,
  TrialAlreadyExtendedError,
  TrialNotActiveError,
} from '../../lib/OrgProvider';

/** Props for TrialSidebarBanner. */
interface TrialSidebarBannerProps {
  /** Days remaining on the trial, rounded up. */
  daysRemaining: number;
  /** Trial end Date used for the "ends {date}" line, or null for generic copy. */
  endsAt: Date | null;
  /** True when we should render the Extend button (<= 3 days + eligible). */
  shouldShowExtendButton: boolean;
  /** True when daysRemaining <= 2; drives a warning tint on the counter. */
  isEndingSoon: boolean;
  /** Whether the viewer holds `organization:manage_billing`. */
  canManageBilling: boolean;
}

function formatEndsAt(endsAt: Date | null): string {
  if (!endsAt) return '';
  try {
    return endsAt.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

/**
 * Renders the trial banner card in the sidebar. Uses the sidebar's dark
 * palette and small-type sizing rather than the billing-card primitive so it
 * reads as a peer of the "Recent Interviews" heading rather than a full page
 * section.
 */
export const TrialSidebarBanner: React.FC<TrialSidebarBannerProps> = ({
  daysRemaining,
  endsAt,
  shouldShowExtendButton,
  isEndingSoon,
  canManageBilling,
}) => {
  const { supabase } = useSupabase();
  const { activeOrg, extendTrial } = useOrg();
  const [openingPortal, setOpeningPortal] = useState(false);
  const [extending, setExtending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleAddPaymentMethod = useCallback(async () => {
    if (!activeOrg?.id) return;
    setOpeningPortal(true);
    setErrorMessage(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setErrorMessage('Please sign in again.');
        return;
      }
      const res = await fetch('/api/billing/portal-session', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'X-Organization-ID': activeOrg.id,
        },
        // Deep-link the Stripe Customer Portal straight to the add-payment-
        // method screen. Without this the portal opens on its home page and
        // the user has to hunt for "Payment methods".
        body: JSON.stringify({ flow_type: 'payment_method_update' }),
      });
      if (!res.ok) {
        throw new Error(`Portal session failed: ${res.status}`);
      }
      const data = await res.json() as { url: string };
      window.location.href = data.url;
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'TrialSidebarBanner.handleAddPaymentMethod',
        { orgId: activeOrg.id },
      );
      setErrorMessage('Could not open billing portal.');
      setOpeningPortal(false);
    }
  }, [supabase, activeOrg?.id]);

  const handleExtend = useCallback(async () => {
    setExtending(true);
    setErrorMessage(null);
    try {
      await extendTrial();
    } catch (err) {
      if (err instanceof TrialAlreadyExtendedError) {
        setErrorMessage('Trial already extended.');
      } else if (err instanceof TrialNotActiveError) {
        setErrorMessage('Trial is no longer active.');
      } else {
        setErrorMessage('Could not extend trial.');
      }
    } finally {
      setExtending(false);
    }
  }, [extendTrial]);

  const endsAtText = formatEndsAt(endsAt);

  return (
    <div
      data-testid="trial-sidebar-banner"
      className="mx-2 mb-2 p-3 rounded-lg border border-warning/30 bg-wash-warning"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-sm font-bold uppercase tracking-wider text-warning-soft">
          Pro Trial
        </span>
        <span
          className={clsx(
            'text-sm font-medium',
            isEndingSoon ? 'text-warning-soft' : 'text-foreground',
          )}
        >
          {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left
        </span>
      </div>
      {endsAtText && (
        <p className="text-sm text-muted-foreground mb-3">Ends {endsAtText}</p>
      )}
      {canManageBilling && (
        <button aria-busy={openingPortal}
          type="button"
          onClick={handleAddPaymentMethod}
          disabled={openingPortal}
          className="w-full rounded-lg px-3 py-2 text-sm font-medium bg-warning text-warning-foreground hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ButtonContent loading={openingPortal}>Add payment method</ButtonContent>
        </button>
      )}
      {canManageBilling && shouldShowExtendButton && (
        <button aria-busy={extending}
          type="button"
          onClick={handleExtend}
          disabled={extending}
          data-testid="trial-extend-button"
          className="w-full mt-2 rounded-lg px-3 py-2 text-sm font-medium border border-warning/60 text-warning-soft hover:bg-warning/10 hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ButtonContent loading={extending}>Extend by 7 days</ButtonContent>
        </button>
      )}
      {errorMessage && (
        <p className="mt-2 text-sm text-danger">{errorMessage}</p>
      )}
    </div>
  );
};
